import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { DropzoneArea } from 'react-mui-dropzone';
import { trim } from 'lodash';
import papaparse from 'papaparse';
import CsvTable from './CsvTable';
import { getCountryCode3ByName } from 'utils/countries';
import { ICsvData, IInvalidData } from './index';
import { LoadingButton } from '@mui/lab';
import { UploadFile } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { useTranslation } from 'react-i18next';

const invalidTableColumn = [
  {
    title: 'Row',
    dataIndex: 'rowNumber',
    key: 'rowNumber',
  },
  {
    title: 'Column',
    dataIndex: 'column',
    key: 'column',
  },
  {
    title: 'Error',
    dataIndex: 'error',
    key: 'error',
  },
];

function validAndConvertCsvData(item: ICsvData): Pick<IInvalidData, 'column' | 'error'> | void {
  if (item.companyNumber) {
    return;
  }
  item.companyName = trim(item.companyName);
  if (item.companyName.length < 2) {
    return {
      error: '"Company Name" does not seem valid',
      column: 'companyName',
    };
  }

  item.countryCode3 = trim(item.countryCode3) || '';

  // valid country code 3 digit letter and number
  if (item.countryCode3?.length !== 3) {
    return {
      error: `"${item.countryCode3}" is not a valid, expect an official country name or Alpha3 Country Code`,
      column: 'country',
    };
  }
}

type IOnUpload = (list: ICsvData[]) => Promise<void>;

export default function CsvUploadSteps({
  onUpload,
  onFinish,
}: {
  onUpload: IOnUpload;
  onFinish: () => void;
}) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [file, setFile] = React.useState<File>();
  const [tabValue, setTabValue] = React.useState('1');
  const [readyCsvData, setReadyCsvData] = React.useState<ICsvData[]>([]);
  const [invalidCsvData, setInvalidCsvData] = React.useState<IInvalidData[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');
  const { t } = useTranslation('default');
  const steps = [t('select-a-csv-file'), t('validate-and-preview')];

  const exampleColumns = [
    {
      title: t('company-name'),
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: t('country-code'),
      dataIndex: 'countryCode3',
      key: 'countryCode3',
    },
    {
      title: t('company-number'),
      dataIndex: 'companyNumber',
      key: 'companyNumber',
    },
    {
      title: t('company-address'),
      dataIndex: 'companyAddress',
      key: 'companyAddress',
    },
    {
      title: t('postal-code'),
      dataIndex: 'postalCode',
      key: 'postalCode',
    },
    {
      title: 'Company Description',
      dataIndex: 'companyDescription',
      key: 'companyDescription',
    },
    {
      title: 'Commodities',
      dataIndex: 'commodities',
      key: 'commodities',
    },
    {
      title: 'Employee Count',
      dataIndex: 'employeeCount',
      key: 'employeeCount',
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleUpload = async () => {
    // do the upload and get result
    setUploading(true);
    try {
      await onUpload(readyCsvData);
    } catch (e) {
      console.error(e);

      setUploadError(e instanceof Error ? e.message : 'Upload failed, please try again later');
    }
    setUploading(false);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const extractCompayListFromCsv = (csvString: string) => {
    const res = papaparse.parse(csvString);
    const headers = res.data[0] as string[];
    const firstCell = headers[0].toLowerCase().trim();
    const hasHead = firstCell === 'external identifier';
    const guessIndex = (name: string, defaultIndex: number): number => {
      const lowerHeaders = headers.map((i) => i.toLocaleLowerCase());
      const idx = lowerHeaders.indexOf(name.toLocaleLowerCase());
      return idx >= 0 ? idx : defaultIndex;
    };
    const rows = res.data as string[][];
    const filterRow = rows.filter((row: string[]) => row.find((element) => !!element)); // reject blank rows
    const filterColumn: [string, string, string, string, string, string, string, string][] =
      filterRow.map((item) => {
        return [
          item[guessIndex('Company Name', 1)],
          item[guessIndex('Company Country', 2)],
          item[guessIndex('External Identifier', 0)],
          item[guessIndex('Company Address', 4)],
          item[guessIndex('Postal Code', 3)],
          item[guessIndex('Company Description', 5)],
          item[guessIndex('Commodities', 6)],
          item[guessIndex('Employee Count', 7)],
        ];
      });
    if (hasHead) {
      // remove the header
      filterColumn.shift();
    }
    return {
      meta: {
        hasHead,
      },
      rows: filterColumn,
    };
  };

  const processContentString = (csvString: string) => {
    const companyNameMap: Map<string, number> = new Map();
    const companyNumberMap: Map<string, number> = new Map();
    const filteredData = extractCompayListFromCsv(csvString);
    const hasHead = filteredData.meta.hasHead;
    const rowNumberOffset = hasHead ? 2 : 1;
    const ready: ICsvData[] = [];
    const invalid: IInvalidData[] = [];
    filteredData.rows.forEach((item, index: number) => {
      const [
        companyName,
        countryInput,
        companyNumber,
        companyAddress,
        postalCode,
        companyDescription,
        commodities,
        employeeCount,
      ] = item;
      const obj: ICsvData = {
        key: index,
        rowNumber: index + rowNumberOffset,
        companyName,
        countryCode3: getCountryCode3ByName(countryInput),
        companyNumber,
        companyAddress,
        postalCode,
        companyDescription,
        commodities,
        employeeCount,
      };
      const companyNameKey = (obj.companyName || '') + (obj.countryCode3 || '');
      const companyNumberKey = (obj.companyNumber || '') + (obj.countryCode3 || '');

      let err = validAndConvertCsvData(obj);

      // check repeat company name
      if (!err && obj.companyName && companyNameMap.has(companyNameKey)) {
        err = {
          column: 'Company Name',
          error: `Company name ${obj.companyName} is a duplication of row ${
            companyNameMap.get(companyNameKey) || 0
          }`,
        };
      }

      // check if duplicated company number exists
      if (!err && obj.companyNumber && companyNumberMap.has(companyNumberKey)) {
        err = {
          column: 'External Identifier',
          error: `External Identifier ${obj.companyNumber} is a duplication of row ${
            companyNumberMap.get(companyNumberKey) || 0
          }`,
        };
      }

      if (!err) {
        ready.push(obj);
        if (obj.companyName) {
          companyNameMap.set(companyNameKey, obj.rowNumber);
        }
        if (obj.companyNumber) {
          companyNumberMap.set(companyNumberKey, obj.rowNumber);
        }
      } else {
        invalid.push({
          key: index,
          rowNumber: index + rowNumberOffset,
          column: err.column,
          error: err.error,
        });
      }
    });

    setReadyCsvData(ready);
    setInvalidCsvData(invalid);
  };

  const readCsv = (f: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(f, 'utf8');
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
    });
  };

  const handleCsvSelected = async (files: File[]) => {
    const file = files[0];
    setFile(file);
    const csvString = await readCsv(file);
    processContentString(csvString);
  };

  const renderCSVUpload = () => {
    return (
      <React.Fragment>
        <Typography sx={{ mt: 2, mb: 1 }}>
          <Typography>
            1. {t('load-the-input-company-list-file-in-a-csv-format-described-below')}
          </Typography>
          <Typography>2. {t('the-input-format-company-name-country-iso-code-3-name')}</Typography>
        </Typography>

        <DropzoneArea
          acceptedFiles={['text/csv']}
          filesLimit={1}
          initialFiles={file ? [file] : []}
          useChipsForPreview
          dropzoneText={t('drag-and-drop-a-csv-file-here-or-click')}
          onChange={(files) => handleCsvSelected(files)}
        />
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button onClick={handleNext}>{t('next')}</Button>
        </Box>
      </React.Fragment>
    );
  };

  const renderCsvReadyData = () => {
    if (!readyCsvData.length) {
      return (
        <Box component="p" sx={{ p: 2, border: '1px solid grey', backgroundColor: '#f1f1f1' }}>
          There is no valid company entity found.
        </Box>
      );
    }
    return <CsvTable columns={exampleColumns} data={readyCsvData} />;
  };

  const renderInvalidCsvData = () => {
    return <CsvTable columns={invalidTableColumn} data={invalidCsvData} />;
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const renderPreview = () => {
    return (
      <React.Fragment>
        <Typography sx={{ mt: 2, mb: 1 }}>
          {t('once-upload-all-the-companies-will-be-verified-by-coriolisesg')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Box sx={{ width: '100%', typography: 'body1' }}>
            <TabContext value={tabValue}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleChangeTab} aria-label="lab API tabs example">
                  <Tab label={`${t('ready-to-import')} (${readyCsvData.length})`} value="1" />
                  <Tab label={`${t('invalid')} (${invalidCsvData.length})`} value="2" />
                </TabList>
              </Box>
              <TabPanel value="1">{renderCsvReadyData()}</TabPanel>
              <TabPanel value="2">{renderInvalidCsvData()}</TabPanel>
            </TabContext>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
            {t('back')}
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />

          <LoadingButton
            disabled={Boolean(invalidCsvData?.length)}
            loading={uploading}
            loadingPosition="start"
            color="info"
            onClick={handleUpload}
            variant="contained"
            startIcon={<UploadFile />}
          >
            {t('upload')}
          </LoadingButton>
        </Box>
      </React.Fragment>
    );
  };

  const renderResult = () => {
    if (uploadError) {
      return (
        <React.Fragment>
          <Alert severity="error">
            <AlertTitle>{t('error')}</AlertTitle>
            {t('there-is-an-error-occured')}: <strong>{uploadError}</strong>
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button onClick={onFinish}>Close</Button>
          </Box>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <Alert severity="success">
          <AlertTitle>{t('uploaded')}</AlertTitle>
          {t('click-the-button-below-to-refresh-the-company-list')}
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button onClick={onFinish}>{t('refresh')}</Button>
        </Box>
      </React.Fragment>
    );
  };

  if (activeStep === steps.length) {
    return <Box sx={{ width: '100%' }}>{renderResult()}</Box>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {activeStep === 0 && renderCSVUpload()}
      {activeStep === 1 && renderPreview()}
    </Box>
  );
}
